import { createContext, createElement, useContext, useEffect } from 'react';
import type { ReactElement, ReactNode } from 'react';

const APP_TITLE = 'FlowQA';
const DEFAULT_DESCRIPTION =
  'Track test runs, surface failure patterns, and understand quality trends across suites, tests, and pipelines.';
const SITE_ORIGIN = 'https://www.flowqa.com';
const DEFAULT_IMAGE_URL = `${SITE_ORIGIN}/logo.png`;
const DEFAULT_IMAGE_ALT = 'FlowQA logo';

export type PageRobotsPolicy = 'index,follow' | 'noindex,follow' | 'noindex,nofollow';

export type PageMetadataOptions = {
  canonicalPath?: string;
  imageAlt?: string;
  imagePath?: string;
  robots?: PageRobotsPolicy;
  twitterCard?: 'summary' | 'summary_large_image';
};

type ResolvedPageMetadata = {
  title: string;
  description: string;
  imageAlt: string;
  imageUrl: string;
  canonicalUrl: string | null;
  robots: PageRobotsPolicy;
  twitterCard: 'summary' | 'summary_large_image';
};

type PageTitleOverrideContextValue = {
  setTitleOverride: (title: string | undefined) => void;
};

type PageTitleOverrideProviderProps = {
  children: ReactNode;
  value: PageTitleOverrideContextValue;
};

const PageTitleOverrideContext = createContext<PageTitleOverrideContextValue | null>(null);

const buildPageTitle = (title?: string): string => {
  const trimmedTitle = title?.trim() ?? '';
  if (!trimmedTitle) {
    return APP_TITLE;
  }
  if (
    trimmedTitle === APP_TITLE ||
    trimmedTitle.startsWith(`${APP_TITLE} ·`) ||
    (trimmedTitle.startsWith('[') && trimmedTitle.endsWith(']'))
  ) {
    return trimmedTitle;
  }
  return `${trimmedTitle} · ${APP_TITLE}`;
};

const resolveDescription = (description?: string): string => {
  const resolvedDescription = description?.trim();
  return resolvedDescription && resolvedDescription.length > 0
    ? resolvedDescription
    : DEFAULT_DESCRIPTION;
};

const resolveCanonicalUrl = (canonicalPath?: string): string | null => {
  const trimmedPath = canonicalPath?.trim();
  if (!trimmedPath) return null;
  return new URL(trimmedPath, SITE_ORIGIN).toString();
};

const resolveImageUrl = (imagePath?: string): string => {
  const trimmedPath = imagePath?.trim();
  if (!trimmedPath) return DEFAULT_IMAGE_URL;
  return new URL(trimmedPath, SITE_ORIGIN).toString();
};

const resolveImageAlt = (imageAlt?: string): string => {
  const trimmedImageAlt = imageAlt?.trim();
  return trimmedImageAlt && trimmedImageAlt.length > 0 ? trimmedImageAlt : DEFAULT_IMAGE_ALT;
};

export const resolvePageMetadata = (
  title?: string,
  description?: string,
  options: PageMetadataOptions = {},
): ResolvedPageMetadata => ({
  title: buildPageTitle(title),
  description: resolveDescription(description),
  imageAlt: resolveImageAlt(options.imageAlt),
  imageUrl: resolveImageUrl(options.imagePath),
  canonicalUrl: resolveCanonicalUrl(options.canonicalPath),
  robots: options.robots ?? 'noindex,nofollow',
  twitterCard: options.twitterCard ?? 'summary',
});

export const PageTitleOverrideProvider = ({
  children,
  value,
}: PageTitleOverrideProviderProps): ReactElement =>
  createElement(PageTitleOverrideContext.Provider, { value }, children);

export const usePageTitle = (title?: string): void => {
  const overrideContext = useContext(PageTitleOverrideContext);

  useEffect(() => {
    if (overrideContext) {
      const trimmedTitle = title?.trim() ?? '';
      overrideContext.setTitleOverride(trimmedTitle.length > 0 ? trimmedTitle : undefined);
      return () => {
        overrideContext.setTitleOverride(undefined);
      };
    }

    document.title = buildPageTitle(title);
    return undefined;
  }, [overrideContext, title]);
};

const getOrCreateMetaTag = (selector: string, attribute: 'name' | 'property'): HTMLMetaElement => {
  const existingTag = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${selector}"]`);
  if (existingTag) {
    return existingTag;
  }
  const tag = document.createElement('meta');
  tag.setAttribute(attribute, selector);
  document.head.appendChild(tag);
  return tag;
};

const setMetaTagContent = (
  selector: string,
  attribute: 'name' | 'property',
  content: string,
): void => {
  getOrCreateMetaTag(selector, attribute).setAttribute('content', content);
};

const removeMetaTag = (selector: string, attribute: 'name' | 'property'): void => {
  document.querySelector<HTMLMetaElement>(`meta[${attribute}="${selector}"]`)?.remove();
};

const setCanonicalLink = (canonicalUrl: string | null): void => {
  const existingLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonicalUrl) {
    existingLink?.remove();
    return;
  }
  if (existingLink) {
    existingLink.setAttribute('href', canonicalUrl);
    return;
  }
  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', canonicalUrl);
  document.head.appendChild(link);
};

export const usePageMetadata = (
  title?: string,
  description?: string,
  options: PageMetadataOptions = {},
): void => {
  useEffect(() => {
    const resolvedMetadata = resolvePageMetadata(title, description, options);

    document.title = resolvedMetadata.title;
    setMetaTagContent('description', 'name', resolvedMetadata.description);
    setMetaTagContent('robots', 'name', resolvedMetadata.robots);
    setCanonicalLink(resolvedMetadata.canonicalUrl);

    setMetaTagContent('og:title', 'property', resolvedMetadata.title);
    setMetaTagContent('og:description', 'property', resolvedMetadata.description);
    setMetaTagContent('og:type', 'property', 'website');
    setMetaTagContent('og:site_name', 'property', APP_TITLE);
    setMetaTagContent('og:image', 'property', resolvedMetadata.imageUrl);
    setMetaTagContent('og:image:alt', 'property', resolvedMetadata.imageAlt);
    setMetaTagContent('twitter:card', 'name', resolvedMetadata.twitterCard);
    setMetaTagContent('twitter:title', 'name', resolvedMetadata.title);
    setMetaTagContent('twitter:description', 'name', resolvedMetadata.description);
    setMetaTagContent('twitter:image', 'name', resolvedMetadata.imageUrl);
    setMetaTagContent('twitter:image:alt', 'name', resolvedMetadata.imageAlt);

    if (resolvedMetadata.canonicalUrl) {
      setMetaTagContent('og:url', 'property', resolvedMetadata.canonicalUrl);
    } else {
      removeMetaTag('og:url', 'property');
    }
  }, [description, options, title]);
};
